import { useId } from "react";
import { LabelPrinterPicker } from "@/components/label-printer-picker";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MAX_PARCELS, MIN_PARCELS, type UseLabelPrintingResult } from "@/hooks/use-label-printing";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The whole dialog is a function of this: nothing here knows what is being labelled. */
  labels: UseLabelPrintingResult;
}

/**
 * The parcel-label dialog itself — shell, count field, printer picker and footer.
 *
 * The job and invoice dialogs rendered byte-for-byte identical markup, differing only in which
 * record they print for; that difference lives entirely in the `useLabelPrinting` options each one
 * passes, so everything downstream of the hook's result is shared here. The same argument that
 * pulled `<LabelPrinterPicker>` out applies harder to this: the description names the print
 * destination, and a second copy is a second place for that sentence to stop matching where the
 * labels actually go.
 *
 * Owns the `<Dialog>` shell as well as its contents. Splitting the shell off would leave the two
 * callers each holding a `<Dialog>` whose props are the same `open`/`onOpenChange` pair — a second
 * copy of nothing useful.
 */
export function ParcelLabelsDialogBody({ open, onOpenChange, labels }: Props) {
  const countId = useId();
  const hintId = useId();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Print parcel labels</DialogTitle>
          <DialogDescription>
            One 100&times;150mm label per parcel, each numbered &ldquo;N of {labels.valid ? labels.parsed : "M"}&rdquo;.{" "}
            {labels.agent
              // The actual-size warning belongs to the PDF path only: ZPL carries
              // absolute dot coordinates, so there is no scaling to get wrong.
              ? <>Labels go straight to {labels.agent.name} on this workstation.</>
              : <>A PDF opens in a new tab. Print at actual size &mdash; scaling to fit will misalign the label on the stock.</>}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label htmlFor={countId}>Number of parcels</Label>
          <Input
            id={countId}
            type="number"
            min={MIN_PARCELS}
            max={MAX_PARCELS}
            value={labels.count}
            aria-describedby={hintId}
            onChange={(e) => labels.setCount(e.target.value)}
          />
          <p id={hintId} className="text-xs text-muted-foreground">
            Between {MIN_PARCELS} and {MAX_PARCELS}.
          </p>
        </div>

        {/* Only with an agent: the PDF path opens a tab, which has no printer to choose. */}
        {labels.agent && <LabelPrinterPicker {...labels.printerPicker} />}

        <DialogFooter>
          {/* Cancel stays enabled while printing on purpose: if the request hangs,
              it's the operator's only escape hatch. */}
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          {/* Secondary, not outline: it sits next to Cancel and must not read as the
              other way to close the dialog. */}
          <Button
            variant="secondary"
            onClick={() => void labels.downloadZpl()}
            disabled={!labels.canSubmit}
          >
            {labels.downloading ? "Downloading…" : "Download ZPL"}
          </Button>
          {/* Deliberately NOT disabled while the agent is being resolved: the probe
              can be slow or hang, and the PDF path works regardless, so blocking on
              it would only stop the operator printing at all. */}
          <Button
            onClick={() => void labels.submit()}
            disabled={!labels.canSubmit}
          >
            {labels.printing ? "Printing…" : "Print labels"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
